export type CalendarDate = { year: number; month: number; day: number };

const DAY = 24 * 60 * 60 * 1000;
const LOCAL = 'local';

const formatters = new Map<string, Intl.DateTimeFormat>();

const createFormatter = (timeZone?: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' });

const getFormatter = (timeZone?: string) => {
  const key = timeZone ?? LOCAL;
  let formatter = formatters.get(key);
  if (!formatter) {
    try {
      formatter = createFormatter(timeZone);
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;
      console.warn(`calendarDate: zona horaria inválida «${timeZone}», se usa la del navegador`);
      formatter = createFormatter();
    }
    formatters.set(key, formatter);
  }
  return formatter;
};

/**
 * Fecha de calendario de `timestamp` en `timeZone` (la del navegador si es `undefined`). Una zona
 * inválida cae a la del navegador con un aviso por consola. Lanza `RangeError` si `timestamp` no es
 * finito.
 */
export const toCalendarDate = (timestamp: number, timeZone?: string): CalendarDate => {
  const parts: Record<string, string> = {};
  for (const { type, value } of getFormatter(timeZone).formatToParts(timestamp))
    parts[type] = value;
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
};

export const calendarDaysBetween = (earlier: CalendarDate, later: CalendarDate) =>
  Math.round(
    (Date.UTC(later.year, later.month - 1, later.day) -
      Date.UTC(earlier.year, earlier.month - 1, earlier.day)) /
      DAY,
  );

const daysInMonth = ({ year, month }: CalendarDate) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Meses completos entre dos fechas: el mes se cumple al llegar al mismo día del mes, o al último
 * día de un mes más corto (31 de enero → 28 de febrero es un mes).
 */
export const fullMonthsBetween = (earlier: CalendarDate, later: CalendarDate) => {
  const months = (later.year - earlier.year) * 12 + (later.month - earlier.month);
  const anniversary = Math.min(earlier.day, daysInMonth(later));
  return later.day < anniversary ? months - 1 : months;
};
